import { formatWithCurrencyNoDecimals } from '@n2-common/lib/money';
import { Hero, Headline, StackedBarChart } from '@n2-common/ui';
import { BaseFieldProps } from '@n2-common/ui/Input';
import { useField } from 'formik';
import { useMemo } from 'react';
import style from './style.module.scss';
import { useTranslation } from 'react-i18next';

export type CliFieldConfig = {
    offerFlex: boolean;
    balance: number;
    creditLimit: number | null;
    maxIncrement: number;
    newLimitField: string;
    minIncrement: number;
    coApplicantName?: string;
    amountAsSlider?: boolean;
};

type AmountVisualizerProps = CountryProps & CliFieldConfig;

export default function AmountVisualizer({
    country,
    newLimitField,
    balance,
    creditLimit,
    maxIncrement,
    offerFlex,
}: AmountVisualizerProps & BaseFieldProps) {
    const { t } = useTranslation('customer-loan');
    const [{ value }] = useField({
        name: newLimitField,
        type: 'number',
    });
    const increase = parseInt(value, 10) || 0;
    const newAvailableAmount = increase + (offerFlex ? (creditLimit || 0) - balance : 0);

    const bars: Parameters<typeof StackedBarChart>[0]['bars'] = useMemo(() => {
        const notUsedCredit = maxIncrement - increase;

        return [
            { legend: t<string>('forms.choose.bar.currentLimit'), value: balance, style: '100%' },
            { legend: t<string>('forms.choose.bar.newLimit'), value: newAvailableAmount, style: '50%' },
            { value: notUsedCredit, style: '0%' },
        ];
    }, [balance, increase, maxIncrement, newAvailableAmount, t]);

    return (
        <Hero ui="quaternary">
            <Headline level={6}>
                {t<string>('forms.choose.newAvailableAmount')} {formatWithCurrencyNoDecimals(newAvailableAmount, country)}
            </Headline>
            <StackedBarChart bars={bars} />
            {offerFlex ? (
                <Headline level={6} className={style.subHeaderHero}>
                    {t<string>('forms.choose.newCreditLimit')} {formatWithCurrencyNoDecimals((creditLimit || 0) + increase - balance, country)}
                </Headline>
            ) : (
                <Headline level={6} className={style.subHeaderHero}>
                    {t<string>('forms.choose.newAmount')} {formatWithCurrencyNoDecimals(balance + increase, country)}
                </Headline>
            )}
        </Hero>
    );
}

export function NumberOfTermsVisualizer(props: { field: string } & BaseFieldProps) {
    const { t } = useTranslation('customer-loan');
    const [{ value }] = useField({
        name: props.field,
        type: 'number',
    });
    return (
        <Hero ui="transparent">
            <Headline level={6}>{props.label}</Headline>
            <Headline level={6} className={style.subHeaderHero}>
                {t<string>('forms.terms-slider.markFormat', { years: value / 12 })}
            </Headline>
        </Hero>
    );
}
